<template>
  <div id="options">
    <v-container fluid>
      <v-flex xs12>
        <HowToUseThisPage />
        <div class="option-container">
          <div class="route-container mb-4 section-heading">
            <h1>
              <v-icon class="option-icon mr-2" :color="iconColor">
                mdi-cogs
              </v-icon>
              {{ $t('adminLanguages.title') }}
            </h1>
          </div>
          <v-flex class="d-flex align-center mb-7 mt-12">
            <v-btn
              color="rgba(165, 216, 103, 1)"
              class="elevation-0 font-weight-bold"
              @click="openCreate()"
            >
              {{ $t('adminLanguages.add') }}
            </v-btn>
          </v-flex>
        </div>

        <v-data-table
          :headers="headers"
          :items="languages"
          item-key="code"
          :items-per-page="50"
        >
          <!-- Enabled -->
          <template #item.enabled="{ item }">
            <div class="tj-cell tj-w-110">
              <v-switch
                class="tj-switch"
                :model-value="item.enabled"
                inset
                density="comfortable"
                hide-details
                :ripple="false"
                color="#A0C548"
                @update:model-value="(val) => save({ ...item, enabled: val })"
              />
            </div>
          </template>

          <!-- RTL -->
          <template #item.rtl="{ item }">
            <div class="tj-cell tj-w-90">
              <v-switch
                class="tj-switch"
                :model-value="item.rtl"
                inset
                density="comfortable"
                hide-details
                :ripple="false"
                color="#A0C548"
                @update:model-value="(val) => save({ ...item, rtl: val })"
              />
            </div>
          </template>

          <!-- Default -->
          <template #item.isDefault="{ item }">
            <div class="tj-cell tj-w-110">
              <v-radio-group
                class="tj-radio"
                :model-value="defaultCode"
                inline
                density="comfortable"
                hide-details
                @update:model-value="(code) => makeDefault(code)"
              >
                <v-radio :value="item.code" color="#A0C548" />
              </v-radio-group>
            </div>
          </template>

          <!-- Row actions -->
          <template #item.actions="{ item }">
            <v-icon @click="openEdit(item)" class="mr-6">edit</v-icon>
            <v-icon v-if="!isHelpDesk" @click="remove(item)">delete_icon</v-icon>
          </template>
        </v-data-table>

        <v-dialog v-model="dialog.open" max-width="480">
          <v-card>
            <v-card-title class="text-h6">{{
              dialog.mode === 'create'
                ? $t('adminLanguages.add')
                : $t('common.edit')
            }}</v-card-title>
            <v-card-text>
              <v-text-field
                v-model="form.code"
                :label="$t('adminLanguages.codeLabel')"
                :disabled="dialog.mode === 'edit'"
                autocapitalize="off"
                autocomplete="off"
                spellcheck="false"
                @update:model-value="
                  (val) => (form.code = (val || '').trim().toLowerCase())
                "
              />
              <v-text-field
                v-model="form.label"
                :label="$t('adminLanguages.labelLabel')"
              />
              <v-text-field
                v-model.number="form.order"
                type="number"
                :label="$t('adminLanguages.orderLabel')"
              />

              <!-- same treatment: fixed box, no ripple, green -->
              <div class="d-flex align-center mt-2 flex-wrap" style="gap: 24px">
                <div class="tj-field">
                  <span class="tj-field-label">Enabled</span>
                  <div class="tj-cell tj-w-110">
                    <v-switch
                      class="tj-switch"
                      v-model="form.enabled"
                      hide-details
                      inset
                      :ripple="false"
                      color="#A0C548"
                    />
                  </div>
                </div>

                <div class="tj-field">
                  <span class="tj-field-label">RTL</span>
                  <div class="tj-cell tj-w-110">
                    <v-switch
                      class="tj-switch"
                      v-model="form.rtl"
                      hide-details
                      inset
                      :ripple="false"
                      color="#A0C548"
                    />
                  </div>
                </div>
              </div>
            </v-card-text>

            <v-card-actions>
              <v-btn
                v-if="dialog.mode === 'edit'"
                color="primary"
                variant="outlined"
                @click="openTranslations()"
              >
                Edit Translations
              </v-btn>
              <v-spacer />
              <v-btn variant="text" @click="dialog.open = false">{{
                $t('common.cancel')
              }}</v-btn>
              <v-btn color="primary" @click="persist()">{{
                $t('common.save')
              }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-flex>
    </v-container>
  </div>
</template>

<script>
  import { ref, computed } from 'vue'
  import { useLanguages } from '@/helpers/useLanguages'
  import { currentTheme } from '@/plugins/vuetify'
  import HowToUseThisPage from '@/components/utilities/HowToUseThisPage'
  import { useI18n } from 'vue-i18n'
  import Swal from 'sweetalert2'
  import { useStore } from 'vuex'
  import { matchesConfirmWord } from '@/helpers/confirmWord'

  export default {
    name: 'AdminLanguages',
    components: {
      HowToUseThisPage, // ⬅️ register here
    },
    setup() {
      // reactive i18n helpers (global scope so it respects your navbar switcher)
      const { t, locale } = useI18n({ useScope: 'global' })
      const store = useStore()

      // use the helper only
      const {
        languages, // array for the table
        defaultLang, // string code, e.g. 'en'
        addLanguage,
        updateLanguage,
        removeLanguage,
        setDefault,
      } = useLanguages()

      // Recompute headers whenever locale changes
      const headers = computed(() => {
        // touch locale to register dependency
        // (some setups need this even though t() is reactive)
        // eslint-disable-next-line no-unused-expressions
        locale.value
        return [
          {
            title: t('adminLanguages.headers.code'),
            key: 'code',
            width: 90,
            align: 'center',
          },
          {
            title: t('adminLanguages.headers.label'),
            key: 'label',
            width: 90,
            align: 'center',
          },
          {
            title: t('adminLanguages.headers.order'),
            key: 'order',
            width: 90,
            align: 'center',
          },
          {
            title: t('adminLanguages.headers.enabled'),
            key: 'enabled',
            width: 110,
            align: 'center',
          },
          {
            title: t('adminLanguages.headers.rtl'),
            key: 'rtl',
            width: 110,
            align: 'center',
          },
          {
            title: t('adminLanguages.headers.default'),
            key: 'isDefault',
            width: 110,
            align: 'center',
          },
          {
            title: t('adminLanguages.headers.actions'),
            key: 'actions',
            width: 120,
            align: 'end',
            sortable: false,
          },
        ]
      })

      const dialog = ref({ open: false, mode: 'create' })
      const form = ref({
        code: '',
        label: '',
        order: 1,
        enabled: true,
        rtl: false,
      })
      // defaultLang is already a code string
      const defaultCode = computed(() => defaultLang.value)

      const iconColor = computed(() =>
        currentTheme.value === 'dark' ? '#cecece' : '#383935'
      )

      const isHelpDesk = computed(() => {
        const roles = store.getters['UserData/getRoles'] || []
        return roles.includes('helpDesk')
      })

      function openCreate() {
        dialog.value = { open: true, mode: 'create' }
        form.value = {
          code: '',
          label: '',
          order: (languages.value.at(-1)?.order || 0) + 1,
          enabled: true,
          rtl: false,
        }
      }
      function openEdit(item) {
        dialog.value = { open: true, mode: 'edit' }
        form.value = {
          code: item.code,
          label: item.label,
          order: item.order,
          enabled: item.enabled,
          rtl: item.rtl,
        }
      }

      function openTranslations() {
        const languageCode = form.value.code
        // Navigate to the language translations page
        window.location.href = `/${t(
          'common.locale'
        )}/languages/${languageCode}/translations`
      }

      async function persist() {
        const f = form.value
        if (!f.code || !f.label) return
        if (dialog.value.mode === 'create') {
          await addLanguage({
            code: f.code,
            label: f.label,
            order: f.order,
            enabled: f.enabled,
            rtl: f.rtl,
            isDefault: false,
          })
        } else {
          await updateLanguage({
            code: f.code,
            label: f.label,
            order: f.order,
            enabled: f.enabled,
            rtl: f.rtl,
          })
        }
        dialog.value.open = false
      }

      async function save(item) {
        await updateLanguage(item)
      }

      async function makeDefault(code) {
        await setDefault(code)
      }

      async function remove(item) {
        if (isHelpDesk.value) {
          const key = 'adminLanguages.deleteNotAllowed'
          const message = t(key)
          Swal.fire({
            title:
              message !== key ? message : 'Help Desk permissions required',
            text:
              message !== key
                ? message
                : 'Help Desk users cannot delete languages.',
            icon: 'info',
          })
          return
        }

        try {
          const confirmed = await Swal.fire({
            title: `You are about to delete the language: <br>"${item.label}" (${item.code})`,
            html: 'This change cannot be undone. If you are sure, type DELETE in all caps below to confirm.',
            input: 'text',
            inputPlaceholder: 'DELETE',
            inputValidator: (value) => {
              return new Promise((resolve) => {
                if (matchesConfirmWord(value, 'DELETE')) {
                  resolve()
                } else {
                  resolve('You need to type DELETE to confirm')
                }
              })
            },
            showCancelButton: true,
            confirmButtonText: 'Yes, Delete it',
            cancelButtonText: 'No, Keep it',
            buttonsStyling: false,
            customClass: {
              confirmButton:
                'swal-confirm-btn text-white text-uppercase text-body-2',
              cancelButton:
                'swal-cancel-btn text-white text-uppercase text-body-2',
            },
            background: currentTheme.value === 'dark' ? '#1e1e1e' : '#fff',
            color: currentTheme.value === 'dark' ? '#f5f5f5' : '#222',
          })

          if (confirmed.isConfirmed) {
            await removeLanguage(item.code)
            Swal.fire({
              title: 'Language Deleted Successfully',
              text: `The language "${item.label}" (${item.code}) has been deleted.`,
              icon: 'success',
              timer: 3000,
              showConfirmButton: false,
              background: currentTheme.value === 'dark' ? '#1e1e1e' : '#fff',
              color: currentTheme.value === 'dark' ? '#f5f5f5' : '#222',
            })
          }
        } catch (error) {
          console.error('Error deleting language:', error)
          Swal.fire({
            title: 'Error',
            text: 'There was an error deleting the language. Please try again.',
            icon: 'error',
            background: currentTheme.value === 'dark' ? '#1e1e1e' : '#fff',
            color: currentTheme.value === 'dark' ? '#f5f5f5' : '#222',
          })
        }
      }

      return {
        languages,
        defaultLang,
        headers,
        dialog,
        form,
        openCreate,
        openEdit,
        openTranslations,
        persist,
        save,
        makeDefault,
        remove,
        defaultCode,
        iconColor,
        isHelpDesk,
      }
    },
  }
</script>

<style scoped>
  /* keep cell width stable and center content */
  .tj-cell {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .tj-w-90 {
    width: 90px;
  }
  .tj-w-110 {
    width: 110px;
  }

  /* remove default control margins */
  .v-selection-control {
    margin: 0 !important;
  }

  /* lock the switch size so it doesn't reflow on toggle */
  .tj-switch {
    /* set explicit, consistent geometry */
    --v-switch-track-width: 56px;
    --v-switch-track-height: 28px;
    --v-switch-thumb-size: 22px;
    --v-selection-control-size: 28px;
    width: 56px; /* match track width */
    min-width: 56px;
  }

  /* optional: make the table layout extra stable */
  .v-table .v-table__wrapper table {
    table-layout: fixed;
  }
  .v-data-table td,
  .v-data-table th {
    white-space: nowrap;
  }
  .tj-radio {
    margin: 0;
    padding: 0;
  }
</style>
